import type { Theme } from '~/components/ui/theme-context'
import { useTranslation } from 'react-i18next'
import { SegmentedControl } from '~/components/SegmentedControl'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { useTheme } from '~/components/ui/hooks/use-theme'
import useTranslations from '~/helpers/store/locale/translation'
import { tourAnchor } from '~/helpers/tour/anchors'

/**
 * Chaque langue s'écrit dans sa propre langue : quelqu'un perdu dans une
 * interface qu'il ne lit pas cherche « English », pas « Anglais ».
 */
const LANGUAGES = [
  { value: 'fr', label: 'Français', lang: 'fr' },
  { value: 'en', label: 'English', lang: 'en' },
] as const

/**
 * Langue et thème de l'interface (#77). Ils vivaient dans le menu utilisateur,
 * sous-menu compris ; ils ont ici leur place, avec le choix « système » que la
 * bascule binaire du menu ne permettait plus de retrouver.
 *
 * Réglages de CET appareil : le thème et la langue sont gardés dans le
 * navigateur, pas sur le compte.
 */
function PreferencesCard() {
  const { t, i18n } = useTranslation()
  const { theme, setTheme } = useTheme()
  // `useTranslations` et non `i18n.changeLanguage` : lui seul persiste le choix.
  const { changeLanguage } = useTranslations()

  const themes: { value: Theme, label: string }[] = [
    { value: 'light', label: t('account.preferences.theme.light') },
    { value: 'dark', label: t('account.preferences.theme.dark') },
    { value: 'system', label: t('account.preferences.theme.system') },
  ]

  return (
    <Card {...tourAnchor('account.preferences')}>
      <CardHeader>
        <CardTitle>{t('account.preferences.title')}</CardTitle>
        <CardDescription>{t('account.preferences.description')}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5 sm:grid-cols-2">
        <div className="grid gap-2">
          <p className="text-sm font-medium">{t('common.language')}</p>
          <SegmentedControl
            label={t('common.language')}
            options={LANGUAGES}
            value={i18n.language.startsWith('en') ? 'en' : 'fr'}
            onChange={changeLanguage}
          />
        </div>
        <div className="grid gap-2">
          <p className="text-sm font-medium">{t('account.preferences.theme')}</p>
          <SegmentedControl
            label={t('account.preferences.theme')}
            options={themes}
            value={theme}
            onChange={setTheme}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export default PreferencesCard
